import { useState, useEffect, useCallback, useRef } from 'react';
import { MediaService } from '@services/media/MediaService';
import type { MediaFile } from '@models/mediaFile.model';
import type { ListMediaResponse } from '@services/media/commands/cmdListMedia';
import { useAssetKit } from '../assetKit';
import type { AssetKitAsset } from '../assetKit';
import { BRAND_IDENTITY_V2, useFeatureFlag } from '@/shared/featureFlags/useFeatureFlag';
import { useMediaOwner } from '../mediaOwnerContext';

interface MediaLibraryModalProps {
  fileType: 'image' | 'video' | 'audio';
  /** Called when media is selected AND loaded (for videos, after first frame is ready) */
  onSelect: (media: MediaFile) => void;
  /** Called when a .mov is uploaded and needs transcoding. Parent handles the placeholder + transcode. */
  onMovUpload?: (media: MediaFile) => void;
  onClose: () => void;
}

const mediaService = new MediaService();

// The video picker leaves out .mov, which needs transcoding first (MPN-1712 D17, a known gap).
// A brand asset's url is presigned, so the extension is read off the path.
const isMov = (url: string) => url.split('?')[0].toLowerCase().endsWith('.mov');

export function MediaLibraryModal({ fileType, onSelect, onMovUpload, onClose }: MediaLibraryModalProps) {
  const kit = useAssetKit();
  const mediaOwnerId = useMediaOwner();
  const [activeTab, setActiveTab] = useState<'my' | 'brand'>('my');
  const [media, setMedia] = useState<MediaFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadPercent, setUploadPercent] = useState(0);
  const [selecting, setSelecting] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const brandIdentityV2 = useFeatureFlag(BRAND_IDENTITY_V2);

  // brand-identity-v2: every brand asset of the type this picker is for, grouped by kind.
  const brandAssets = (kit?.assets ?? []).filter(
    (asset) => asset.mediaType === fileType && !(fileType === 'video' && isMov(asset.url)),
  );
  const hasBrandTab = brandIdentityV2 ? brandAssets.length > 0 : fileType === 'image' && !!kit?.images?.length;

  const loadMedia = useCallback(async () => {
    setLoading(true);
    try {
      const response: ListMediaResponse = await mediaService.listMedia({
        file_type: fileType,
        name: search || undefined,
        page,
        page_size: 24,
        uploaded_by: mediaOwnerId,
        ...(fileType === 'video' ? { exclude_extension: 'mov' } : {}),
      });
      setMedia(response.results);
      setTotal(response.total);
    } catch (e) {
      console.error('Failed to load media:', e);
    }
    setLoading(false);
  }, [fileType, search, page]);

  useEffect(() => { loadMedia(); }, [loadMedia]);

  const handleUpload = async (files: FileList | File[]) => {
    setUploading(true);
    setUploadPercent(0);
    try {
      for (const file of Array.from(files)) {
        const isMov = file.name.toLowerCase().endsWith('.mov');
        const uploaded = await mediaService.uploadMedia(file, file.name, undefined, (percent) => {
          setUploadPercent(percent);
        });

        if (isMov && onMovUpload) {
          onMovUpload(uploaded);
          onClose();
          return;
        }
      }
      await loadMedia();
    } catch (e) {
      console.error('Upload failed:', e);
    }
    setUploading(false);
    setUploadPercent(0);
  };

  const handleSelect = (item: MediaFile) => {
    if (selecting) return;
    const src = item.storage_uri;
    if (!src) { onSelect(item); return; }

    if (fileType === 'video') {
      // Wait for video to load before closing
      setSelecting(true);
      const vid = document.createElement('video');
      vid.crossOrigin = 'anonymous';
      vid.preload = 'auto';
      vid.onloadeddata = () => {
        onSelect(item);
        setSelecting(false);
      };
      vid.onerror = () => {
        onSelect(item);
        setSelecting(false);
      };
      vid.src = src;
    } else {
      // Images load fast enough — just select
      onSelect(item);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length > 0) handleUpload(e.dataTransfer.files);
  };

  const handlePaste = useCallback((e: ClipboardEvent) => {
    const files = e.clipboardData?.files;
    if (files && files.length > 0) handleUpload(files);
  }, []);

  useEffect(() => {
    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [handlePaste]);

  const pageCount = Math.ceil(total / 24);

  return (
    <div style={backdropStyle} onClick={(e) => { if (e.target === e.currentTarget && !selecting) onClose(); }}>
      <div style={modalStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
            {fileType === 'image' ? 'Image Library' : fileType === 'video' ? 'Video Library' : 'Audio Library'}
          </h3>
          <button onClick={() => !selecting && onClose()} style={closeBtnStyle}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 4l8 8M12 4l-8 8" />
            </svg>
          </button>
        </div>

        {/* Search + Upload (My Media tab only) */}
        {activeTab === 'my' && (
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0', display: 'flex', gap: 8 }}>
            <input
              placeholder={`Search ${fileType}s...`}
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              style={searchInputStyle}
            />
            <button onClick={() => fileInputRef.current?.click()} disabled={uploading} style={uploadBtnStyle}>
              {uploading ? `Uploading ${Math.round(uploadPercent)}%` : 'Upload'}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept={fileType === 'image' ? 'image/*' : fileType === 'video' ? 'video/*' : 'audio/*'}
              multiple
              style={{ display: 'none' }}
              onChange={(e) => e.target.files && handleUpload(e.target.files)}
            />
          </div>
        )}

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', padding: '0 16px' }}>
          {(['my', ...(hasBrandTab ? ['brand'] : [])] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab as any)}
              style={{
                background: 'none', border: 'none', borderBottom: activeTab === tab ? '2px solid #2563eb' : '2px solid transparent',
                padding: '6px 12px', fontSize: 12, fontWeight: activeTab === tab ? 600 : 400,
                color: activeTab === tab ? '#2563eb' : '#64748b', cursor: 'pointer',
              }}
            >
              {tab === 'my' ? 'My Media' : 'Brand'}
            </button>
          ))}
        </div>

        {/* Loading overlay for video selection */}
        {selecting && (
          <div style={{
            position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.8)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 10, borderRadius: 12,
          }}>
            <div style={{ fontSize: 14, color: '#2563eb', fontWeight: 600 }}>Loading video...</div>
          </div>
        )}

        {/* Content area */}
        {activeTab === 'my' ? (
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            style={{
              flex: 1, overflow: 'auto', padding: 16, position: 'relative',
              background: dragOver ? '#eff6ff' : '#ffffff', transition: 'background 0.15s',
            }}
          >
            {dragOver && <div style={dropOverlayStyle}>Drop {fileType}s here to upload</div>}
            {loading ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>Loading...</div>
            ) : media.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>
                <div style={{ fontSize: 14, marginBottom: 8 }}>No {fileType}s found</div>
                <div style={{ fontSize: 12 }}>Drag & drop, paste, or click Upload</div>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12 }}>
                {media.map((item) => (
                  <MediaCard key={item.id} item={item} fileType={fileType} onClick={() => handleSelect(item)} />
                ))}
              </div>
            )}
          </div>
        ) : (
          <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
            {brandIdentityV2 ? (
              <BrandAssetGroups
                assets={brandAssets}
                fileType={fileType}
                onSelect={(asset) =>
                  handleSelect({ id: '', storage_uri: asset.url, file_type: fileType, name: asset.name } as MediaFile)
                }
              />
            ) : (
              <BrandImageGrid
                images={kit?.images ?? []}
                onSelect={(url, name) => {
                  onSelect({ id: '', storage_uri: url, file_type: fileType, name } as any);
                }}
              />
            )}
          </div>
        )}

        {/* Pagination */}
        {activeTab === 'my' && pageCount > 1 && (
          <div style={{ padding: '8px 16px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'center', gap: 8 }}>
            <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1} style={pageBtnStyle}>Prev</button>
            <span style={{ fontSize: 12, color: '#64748b', alignSelf: 'center' }}>{page} / {pageCount}</span>
            <button onClick={() => setPage(Math.min(pageCount, page + 1))} disabled={page >= pageCount} style={pageBtnStyle}>Next</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Media Card with IntersectionObserver ─────────────────

function MediaCard({ item, fileType, onClick }: { item: MediaFile; fileType: string; onClick: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  const url = item.storage_uri;

  // IntersectionObserver for lazy loading
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { rootMargin: '100px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      onClick={onClick}
      style={{
        borderRadius: 8, border: '1px solid #e2e8f0', overflow: 'hidden',
        cursor: 'pointer', transition: 'border-color 0.15s, box-shadow 0.15s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = '#2563eb';
        e.currentTarget.style.boxShadow = '0 0 0 2px rgba(37,99,235,0.15)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = '#e2e8f0';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <div style={{
        width: '100%', height: 100, background: '#f8fafc',
        display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
      }}>
        {!inView ? (
          // Placeholder until in view
          <div style={{ width: '100%', height: '100%', background: '#f1f5f9' }} />
        ) : url ? (
          fileType === 'image' ? (
            <img src={url} alt={item.name} loading="lazy" crossOrigin="anonymous" style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 8 }} />
          ) : fileType === 'audio' ? (
            <AudioThumbnail />
          ) : (
            // Videos: show poster/thumbnail, don't load the video itself
            <VideoThumbnail src={url} />
          )
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <path d="M21 15l-5-5-6 6-3-3-4 4" />
          </svg>
        )}
      </div>
      <div style={{
        padding: '6px 8px', fontSize: 11, color: '#475569',
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}>
        {item.name}
      </div>
    </div>
  );
}

// ─── Audio Thumbnail (waveform glyph placeholder) ─────────

function AudioThumbnail() {
  return (
    <div style={{
      width: '100%', height: '100%', background: 'linear-gradient(135deg, #1e293b, #475569)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth="1.5" strokeLinecap="round">
        <path d="M3 12h2M7 8v8M11 5v14M15 9v6M19 11v2" />
      </svg>
    </div>
  );
}

// ─── Video Thumbnail (native browser poster frame) ────────

function VideoThumbnail({ src }: { src: string }) {
  return (
    <div style={{ width: '100%', height: '100%', position: 'relative', background: '#1e293b' }}>
      <video
        src={src}
        muted
        playsInline
        preload="metadata"
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      />
      <div style={{
        position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
        pointerEvents: 'none',
      }}>
        <svg width="20" height="20" viewBox="0 0 20 20" fill="rgba(255,255,255,0.8)" stroke="none">
          <polygon points="7,4 16,10 7,16" />
        </svg>
      </div>
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────

// ─── Brand Image Grid ─────────────────────────────────────

function BrandImageGrid({ images, onSelect }: { images: { name: string; url: string }[]; onSelect: (url: string, name: string) => void }) {
  if (images.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>
        <div style={{ fontSize: 14, marginBottom: 8 }}>No brand images</div>
      </div>
    );
  }
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12 }}>
      {images.map((img, i) => (
        <div
          key={i}
          onClick={() => onSelect(img.url, img.name)}
          style={{
            borderRadius: 8, border: '1px solid #e2e8f0', overflow: 'hidden',
            cursor: 'pointer', transition: 'border-color 0.15s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#2563eb'; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; }}
        >
          <div style={{ width: '100%', height: 100, background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
            <img src={img.url} alt={img.name} crossOrigin="anonymous" style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 8 }} />
          </div>
          <div style={{ padding: '6px 8px', fontSize: 11, color: '#475569', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {img.name}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Brand Asset Groups (brand-identity-v2) ───────────────

function BrandAssetGroups({ assets, fileType, onSelect }: {
  assets: AssetKitAsset[];
  fileType: string;
  onSelect: (asset: AssetKitAsset) => void;
}) {
  const groups = [...new Set(assets.map((asset) => asset.group))];
  return (
    <>
      {groups.map((group) => (
        <section key={group} aria-label={group} style={{ marginBottom: 16 }}>
          <h4 style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 600, color: '#475569' }}>{group}</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12 }}>
            {assets.filter((asset) => asset.group === group).map((asset) => (
              <MediaCard
                key={asset.url}
                item={{ id: '', storage_uri: asset.url, file_type: fileType, name: asset.name } as MediaFile}
                fileType={fileType}
                onClick={() => onSelect(asset)}
              />
            ))}
          </div>
        </section>
      ))}
    </>
  );
}

const backdropStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000,
};

const modalStyle: React.CSSProperties = {
  background: '#fff', borderRadius: 12, width: 680, height: 640,
  display: 'flex', flexDirection: 'column', overflow: 'hidden',
  boxShadow: '0 8px 30px rgba(0,0,0,0.12)', position: 'relative',
};

const headerStyle: React.CSSProperties = {
  padding: '12px 16px', borderBottom: '1px solid #e2e8f0',
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
};

const closeBtnStyle: React.CSSProperties = {
  background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 4,
};

const searchInputStyle: React.CSSProperties = {
  flex: 1, padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 6,
  fontSize: 13, outline: 'none', background: '#f8fafc', color: '#1e293b',
};

const uploadBtnStyle: React.CSSProperties = {
  padding: '6px 16px', background: '#2563eb', color: '#fff', border: 'none',
  borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer',
};

const pageBtnStyle: React.CSSProperties = {
  padding: '4px 12px', background: '#f1f5f9', border: '1px solid #e2e8f0',
  borderRadius: 4, fontSize: 11, cursor: 'pointer', color: '#475569',
};

const dropOverlayStyle: React.CSSProperties = {
  position: 'absolute', inset: 16, border: '2px dashed #2563eb', borderRadius: 8,
  background: 'rgba(37,99,235,0.05)', display: 'flex', alignItems: 'center',
  justifyContent: 'center', color: '#2563eb', fontSize: 14, fontWeight: 600, zIndex: 5,
};
